import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormRow } from '@/containers/forms/FormRow'
import {
  Box,
  FormControl,
  HStack,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  UnorderedList
} from '@chakra-ui/react'
import { CheckboxFormControl, CustomCollapse, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import type {
  InsuranceParamsPropertyDto,
  PolicyHolderDto
} from 'BrokerTsLib/src/dtos'
import { Insurer, Product, RiskVariant, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { FormLabel } from '@/components'
import { HorizontalRadios } from '@/components/HorizontalRadios'
import { SectionHeading } from '@/components/Section/SectionHeading'
import { FormBody } from '@/containers/forms/FormBody'
import { PersonForm } from '@/containers/forms/PersonForm'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useGetInsurerServiceTypes, useInsurers, useSelections } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'

export const InsureeForm = () => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()
  const { isInsurerAvailable, isInsurersAvailable } = useInsurers(false, null)
  const { data: csobServiceTypes } = useGetInsurerServiceTypes(Insurer.Csob)
  const { data: dataVztah } = useSelections(SelectionType.VztahKPremetuPojisteni)

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    setValue,
    watch,
    formState: { errors },
  } = methods

  const insuredPersonSameAs = watch('policyHolder.insuredPersonSameAs')
  const phType = watch('policyHolder.person.type')
  const propRelationId = watch('property.data.propertyRelationId')
  const variant = watch('property.type')

  const concurrentInsuranceWithOtherInsurer = watch('property.data.concurrentInsuranceWithOtherInsurer')

  const nameof = useNameof<InsuranceParamsPropertyDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))

  useEffect(() => {
    if (insuredPersonSameAs == Selection.PersonSameAsPolicyHolder)
      setValue('policyHolder.insuredPersonSameAs', Selection.PersonSameAsPolicyHolder)
  }, [insuredPersonSameAs])

  return (
    <>
      <FormBody>
        <PersonForm
          personPrefix={phnameof((x) => x.person)}
          disabled={isDisabled}
          showVatPayer={true}
          showAddress={product == Product.LiabilityInsurance}
        />

        {product == Product.PropertyInsurance &&
          <>
            <Stack>
              <CheckboxFormControl name='property.data.propertyValorisationAgreement'
                label='Souhlas s valorizací / indexací pojistné částky'
                labelProps={{ hint: 'Klient souhlasí s valorizací / indexací pojistné částky domácnosti či stavby pojišťovnou.' }}
                isDisabled={isDisabled}
              />

              <CheckboxFormControl name='property.data.insuranceInNewPrice'
                label='Pojištění na novou cenu'
                labelProps={{ hint: 'Zašktněte, pokud klient preferuje pojistné plnění v nové ceně. Některé pojišťovny nenabízí možnost volby (nová nebo obvyklá cena) a poskytují plnění podle všeobecných pojistných podmínek.' }}
                isDisabled={isDisabled}
              />

              {isInsurersAvailable([Insurer.Csob]) && (
                <CheckboxFormControl name='property.data.csobBaScoring'
                  label='ČSOB BA skóring'
                  labelProps={{ hint: 'Sleva 5% na pojistné u ČSOB, pokud klient souhlasí s ověřením skóringu v ČSOB bance.' }}
                  isDisabled={isDisabled}
                />)}

              {(isInsurersAvailable([Insurer.Slavia, Insurer.Axa]) ||
                (isInsurersAvailable([Insurer.Csob]) &&
                  csobServiceTypes?.data &&
                  csobServiceTypes.data.some((it) => it.name == 'WS'))) && (
                <CheckboxFormControl name='policyHolder.propojistenost'
                  label='Sleva za propojištěnost'
                  labelProps={{ hint: 'Sleva na pojistné u dané pojišťovny pokud klient má již sjednanou jinou pojistnou smlouvu.' }}
                  isDisabled={isDisabled}
                />
              )}

              {isInsurersAvailable([Insurer.KB]) && (
                <>
                  <CheckboxFormControl name='property.data.concurrentInsuranceWithOtherInsurer'
                    label='Souběh pojištění'
                    labelProps={{ hint: 'Pro KB pojišťovnu - po dobu souběhu stávajícího pojištění a nového pojištění u KB pojišťovny je její pojištění zdarma. Předpokladem je výběr roční frekvence platby.' }}
                    isDisabled={isDisabled}
                    onChange={(checked) => setValue('property.data.concurrentInsuranceWithOtherInsurer', checked as boolean)}
                  />
                  <FormRow>
                    {concurrentInsuranceWithOtherInsurer && (variant == RiskVariant.Household || variant == RiskVariant.HouseholdAndRealty) && (
                      <FormControl isInvalid={!!errors.property?.data?.concurrentHouseholdInsuranceValidTo}>
                        <FormLabel>Pojištění domácnosti platné do</FormLabel>
                        <Datepicker
                          isDisabled={isDisabled}
                          name='property.data.concurrentHouseholdInsuranceValidTo'
                        />
                      </FormControl>                    
                    )}
                    {concurrentInsuranceWithOtherInsurer && (variant == RiskVariant.Realty || variant == RiskVariant.HouseholdAndRealty) && (                    
                      <FormControl isInvalid={!!errors.property?.data?.concurrentRealtyInsuranceValidTo}>
                        <FormLabel>Pojištění nemovitosti platné do</FormLabel>
                        <Datepicker
                          isDisabled={isDisabled}
                          name='property.data.concurrentRealtyInsuranceValidTo'
                        />
                      </FormControl>
                    )}
                  </FormRow>
                </>
              )}

            </Stack>

            {isInsurersAvailable([Insurer.GeneraliCeskaPojistovna]) &&
              isIn(phType, Selection.Company, Selection.SelfEmployed) && (
              <>
                <Stack direction="column">

                  <FormLabel>
                    Splňuje pojistník alespoň 2 ze 3 níže uvedených podmínek?
                  </FormLabel>

                  <Box>
                    <UnorderedList spacing={1}>
                      <ListItem>
                        Je čistý obrat min. 13 600 000 EUR (cca 340 000 000 Kč)?
                      </ListItem>
                      <ListItem>
                        Je úhrn rozvahy min. 6 600 000 EUR (cca 165 000 000 Kč)?
                      </ListItem>
                      <ListItem>
                        Je průměrný roční stav zaměstnanců min. 250?
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <FormControl>
                    <RadioGroup
                      value={
                        watch('property.data.bigRiskCompany') == true
                          ? 'yes'
                          : watch('property.data.bigRiskCompany') == false
                            ? 'no'
                            : undefined
                      }
                      onChange={(val) =>
                        setValue('property.data.bigRiskCompany', val === 'yes')
                      }
                      isDisabled={isDisabled}
                    >
                      <HStack>
                        <Radio value="yes">Ano</Radio>
                        <Radio value="no">Ne</Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </>
            )}

            <Stack gap={6}>
              <RadioGroupFormControl name='property.data.propertyRelationId' valueAsNumber
                label='Vztah k předmětu pojištění'
                labelProps={{ hint: 'Určení vztahu k předmětu pojištění a také způsobu, jak je majetek užíván.' }}
                isDisabled={isDisabled}
              >
                <HorizontalRadios options={dataVztah?.data.map((item) => ({ value: item.id.toString(), label: item.description }))} />
              </RadioGroupFormControl>

              <CustomCollapse in={isInsurerAvailable(Insurer.Pillow) && propRelationId == Selection.Pronajimatel}        >
                <RadioGroupFormControl name='property.data.longTimeRent' valueAsBoolean isRequired
                  label='Je majetek pronajímán dlouhodobě?'
                  isDisabled={isDisabled}>
                  <HorizontalRadios options={[
                    { value: 'true', label: 'Ano' },
                    { value: 'false', label: 'Ne' }
                  ]} />
                </RadioGroupFormControl>
              </CustomCollapse>
            </Stack>
          </>
        }

        {/* Pojištěný */}
        <Stack columnGap={8} rowGap={4}>
          <SectionHeading sx={{ width: 'auto' }}>Pojištěný</SectionHeading>
          <RadioGroupFormControl name='policyHolder.insuredPersonSameAs' 
            valueAsNumber 
            zeroAsNull
            isDisabled={isDisabled}
          >
            <HorizontalRadios options={[
              { value: Selection.PersonSameAsPolicyHolder.toString(), label: 'Stejný s pojistníkem' },
              { value: '0', label: 'Jiný' }
            ]} />
          </RadioGroupFormControl>
        </Stack>

        {!insuredPersonSameAs && (
          <Box backgroundColor='gray.100' p={7}>
            <FormBody>
              <PersonForm
                personPrefix={phnameof((x) => x.insuredPerson)}
                disabled={isDisabled}
                showAddress={product == Product.LiabilityInsurance}
              />
            </FormBody>
          </Box>
        )}

      </FormBody >
    </>
  )
}
